import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { Camera, CheckCircle, Package, AlertCircle, ScanLine } from 'lucide-react';

const QRScanner = ({ user, onLogout }) => {
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    return () => {
      if (scanner) {
        scanner.clear().catch(console.error);
      }
    };
  }, [scanner]);

  const startScanning = () => {
    setScanning(true);
    setScannedData(null);
    setScanResult(null);

    // Wait for DOM element to be available
    setTimeout(() => {
      const qrReaderElement = document.getElementById("qr-reader");
      if (!qrReaderElement) {
        toast.error('Error: No se pudo inicializar el escáner');
        setScanning(false);
        return;
      }

      try {
        const html5QrcodeScanner = new Html5QrcodeScanner(
          "qr-reader",
          { 
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0
          },
          false
        );

        html5QrcodeScanner.render(onScanSuccess, onScanError);
        setScanner(html5QrcodeScanner);
      } catch (error) {
        console.error('Scanner initialization error:', error);
        toast.error('Error al inicializar el escáner');
        setScanning(false);
      }
    }, 100); // Small delay to ensure DOM is ready
  };

  const stopScanning = () => {
    if (scanner) {
      scanner.clear().catch((error) => {
        console.error('Error clearing scanner:', error);
      });
      setScanner(null);
    }
    setScanning(false);
  };

  const onScanSuccess = (decodedText) => {
    setScannedData(decodedText);
    stopScanning();
    processQRCode(decodedText);
  };

  const onScanError = (error) => {
    // Silently handle scan errors (they occur frequently during scanning)
  };

  const processQRCode = async (qrCode) => {
    // Detect secure QR (JSON with code + signature + package_id)
    let parsed = null;
    try {
      const maybe = JSON.parse(qrCode);
      if (maybe && maybe.code && maybe.signature && maybe.package_id) {
        parsed = maybe;
      }
    } catch (_) {
      // not JSON → treat as legacy code
    }

    if (parsed) {
      try {
        const validate = await axios.post(
          `/packages/validate-qr?code=${encodeURIComponent(parsed.code)}`,
        );
        if (!validate.data?.valid) {
          throw new Error('Firma inválida');
        }
        const pkgRes = await axios.get(`/packages/${parsed.package_id}`);
        const orderRes = await axios.get(`/orders/${pkgRes.data.order_id}`);
        setScanResult({
          success: true,
          secure: true,
          package: pkgRes.data,
          order: orderRes.data,
        });
        toast.success('QR Seguro validado correctamente');
        return;
      } catch (error) {
        setScanResult({
          success: false,
          secure: true,
          error: error.response?.data?.detail || error.message || 'QR seguro inválido',
        });
        toast.error(error.response?.data?.detail || 'QR seguro inválido');
        return;
      }
    }

    // Legacy QR
    try {
      const response = await axios.post('/packages/scan', {
        qr_code: qrCode,
        actor_role: user.role,
        actor_id: user.id,
        lat: null,
        lng: null
      });

      setScanResult({
        success: true,
        package: response.data.package,
        order: response.data.order
      });

      toast.success('¡QR escaneado exitosamente!');
    } catch (error) {
      setScanResult({
        success: false,
        error: error.response?.data?.detail || 'Error al procesar QR'
      });
      toast.error('Error al procesar el código QR');
    }
  };

  const handleManualScan = () => {
    if (!manualCode.trim()) {
      toast.error('Ingresa un código QR válido');
      return;
    }
    processQRCode(manualCode.trim());
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Escáner de QR">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="qr-scanner-container">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#224049' }}>Escáner de Códigos QR</h1>
          <p className="text-sm" style={{ color: '#666' }}>Escanea códigos QR para registrar la cadena de custodia</p>
        </div>

        {/* Role Badge */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full" style={{ backgroundColor: '#0F7A5B' }}>
                <Camera className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold" style={{ color: '#224049' }}>Rol Actual: {user.role}</p>
                <p className="text-xs" style={{ color: '#666' }}>Los escaneos se registrarán bajo este rol</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scanner Card */}
        <Card>
          <CardHeader>
            <CardTitle style={{ color: '#224049' }}>Cámara de Escaneo</CardTitle>
          </CardHeader>
          <CardContent>
            {!scanning && !scanResult && (
              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center py-12" style={{ backgroundColor: '#EAE0D5', borderRadius: '8px' }}>
                  <ScanLine className="w-16 h-16 mb-4" style={{ color: '#0F7A5B' }} />
                  <p className="mb-4" style={{ color: '#666' }}>Presiona el botón para iniciar el escaneo</p>
                  <Button
                    onClick={startScanning}
                    style={{ backgroundColor: '#224049', color: '#D4AD6A' }}
                    data-testid="start-scan-button"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Iniciar Escáner
                  </Button>
                </div>

                {/* Manual Input */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EBE3' }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: '#224049' }}>Ingreso Manual</p>
                  <p className="text-xs mb-3" style={{ color: '#666' }}>Si no puedes escanear, ingresa el código manualmente</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="UID-XXXXXXXXXXXX"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-md"
                      data-testid="manual-code-input"
                    />
                    <Button
                      onClick={handleManualScan}
                      style={{ backgroundColor: '#0F7A5B', color: 'white' }}
                      data-testid="manual-scan-button"
                    >
                      Procesar
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {scanning && (
              <div className="space-y-4">
                <div id="qr-reader" style={{ width: '100%' }} data-testid="qr-reader-area"></div>
                <Button
                  onClick={stopScanning}
                  variant="outline"
                  className="w-full"
                  data-testid="stop-scan-button"
                >
                  Detener Escaneo
                </Button>
              </div>
            )}

            {scanResult && (
              <div className="space-y-4">
                {scanResult.success ? (
                  <div className="space-y-4">
                    {/* Success Message */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#E8F5E9', border: '2px solid #4CAF50' }}>
                      <div className="flex items-center gap-3 mb-2">
                        <CheckCircle className="w-6 h-6" style={{ color: '#2E7D32' }} />
                        <p className="font-bold" style={{ color: '#2E7D32' }}>¡Escaneo Exitoso!</p>
                        {scanResult.secure && (
                          <span
                            className="ml-auto px-2 py-1 rounded text-xs font-bold"
                            style={{ backgroundColor: '#8E44AD', color: 'white' }}
                            data-testid="secure-qr-badge"
                          >
                            🔐 QR SEGURO (HMAC)
                          </span>
                        )}
                      </div>
                      <p className="text-sm" style={{ color: '#2E7D32' }}>
                        {scanResult.secure
                          ? 'QR Seguro validado y consumido. Firma HMAC verificada.'
                          : 'El código QR ha sido registrado correctamente en la cadena de custodia'}
                      </p>
                    </div>

                    {/* Package Info */}
                    <Card style={{ backgroundColor: '#EAE0D5' }}>
                      <CardHeader>
                        <CardTitle className="text-lg" style={{ color: '#224049' }}>Información del Paquete</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Código QR</p>
                          <p className="font-bold" style={{ color: '#224049' }}>{scanResult.package.qr_code}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Código de Sello</p>
                          <p className="font-semibold" style={{ color: '#0F7A5B' }}>{scanResult.package.seal_code}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Estado del Paquete</p>
                          <span className="inline-block px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#D4AD6A', color: '#224049' }}>
                            {scanResult.package.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Order Info */}
                    <Card style={{ backgroundColor: '#F0EBE3' }}>
                      <CardHeader>
                        <CardTitle className="text-lg" style={{ color: '#224049' }}>Información del Pedido</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>ID del Pedido</p>
                          <p className="font-semibold" style={{ color: '#224049' }}>{scanResult.order.id.substring(0, 16)}...</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Cliente</p>
                          <p className="font-semibold" style={{ color: '#224049' }}>{scanResult.order.customer.name}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Estado del Pedido</p>
                          <span className="status-badge" style={{ backgroundColor: '#0F7A5B', color: 'white' }}>
                            {scanResult.order.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-medium" style={{ color: '#666' }}>Modo de Entrega</p>
                          <span className={scanResult.order.mode === 'EXPRESS' ? 'mode-express' : 'mode-standard'}>
                            {scanResult.order.mode === 'EXPRESS' ? 'EXPRESS 60\'' : 'STANDARD'}
                          </span>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setScanResult(null);
                          setScannedData(null);
                        }}
                        className="flex-1"
                        style={{ backgroundColor: '#224049', color: '#D4AD6A' }}
                        data-testid="scan-another-button"
                      >
                        Escanear Otro
                      </Button>
                      <Button
                        onClick={() => window.location.href = `/orders/${scanResult.order.id}`}
                        className="flex-1"
                        variant="outline"
                        data-testid="view-order-button"
                      >
                        Ver Pedido Completo
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Error Message */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFEBEE', border: '2px solid #D32F2F' }}>
                      <div className="flex items-center gap-3 mb-2">
                        <AlertCircle className="w-6 h-6" style={{ color: '#D32F2F' }} />
                        <p className="font-bold" style={{ color: '#D32F2F' }}>Error al Procesar QR</p>
                      </div>
                      <p className="text-sm" style={{ color: '#D32F2F' }}>
                        {scanResult.error}
                      </p>
                    </div>

                    <Button
                      onClick={() => {
                        setScanResult(null);
                        setScannedData(null);
                      }}
                      className="w-full"
                      style={{ backgroundColor: '#224049', color: '#D4AD6A' }}
                    >
                      Intentar Nuevamente
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle style={{ color: '#224049' }}>Instrucciones de Uso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {user.role === 'rider' && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9' }}>
                  <p className="font-semibold mb-2" style={{ color: '#2E7D32' }}>📦 Para Riders:</p>
                  <ul className="text-sm space-y-1" style={{ color: '#2E7D32' }}>
                    <li>• Escanea el QR al RECOGER el paquete en el dispensario</li>
                    <li>• Verifica que el sello esté intacto</li>
                    <li>• El escaneo registra la custodia del paquete a tu nombre</li>
                  </ul>
                </div>
              )}
              {user.role === 'customer' && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E3F2FD' }}>
                  <p className="font-semibold mb-2" style={{ color: '#1976D2' }}>📍 Para Clientes:</p>
                  <ul className="text-sm space-y-1" style={{ color: '#1976D2' }}>
                    <li>• Escanea el QR al RECIBIR tu paquete</li>
                    <li>• Verifica tu PIN de entrega con el rider</li>
                    <li>• El escaneo confirma la entrega exitosa</li>
                  </ul>
                </div>
              )}
              {(user.role === 'admin' || user.role === 'dispensary_owner' || user.role === 'support') && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#FFF3E0' }}>
                  <p className="font-semibold mb-2" style={{ color: '#F57C00' }}>🔍 Para Admin/Dispensario/Soporte:</p>
                  <ul className="text-sm space-y-1" style={{ color: '#F57C00' }}>
                    <li>• Puedes escanear QR para verificar el estado de paquetes</li>
                    <li>• Verifica la cadena de custodia completa</li>
                    <li>• Audita los movimientos de los paquetes</li>
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default QRScanner;